/**
 * "Le mapping est le contrat, pas de la documentation" — this table is
 * executed on every exchange.
 *
 * One entity at a time, and the reason is not screen space: a mapping is read
 * and edited against one external system's export, column by column, and a
 * combined table of every entity's rows would be sorted by something nobody
 * is thinking about.
 *
 * **The portal field is a select, not a text box.** The API says which fields
 * a synchronisation can actually write on this entity, so a typo is
 * impossible rather than discovered on the anomaly screen at two in the
 * morning. `— ignorer ce champ —` is a real option: an arriving field
 * deliberately dropped is a mapping decision, and a text box would make it
 * look like an oversight.
 *
 * **The direction is per row and overrides the entity's.** That is the whole
 * point of the column: an installation can take an article's designation from
 * the ERP and keep its own web description, on the same entity.
 *
 * **The transformation's argument is validated by the server as it is typed.**
 * A date format nobody supports fails here, on the screen that entered it,
 * rather than on the first import.
 */

import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { Field } from "@hardauto/core-forms";
import {
  Badge,
  Button,
  Card,
  DataTable,
  Input,
  Page,
  Select,
  StatusDot,
  useToast,
  type Column,
} from "@hardauto/core-ui";

import {
  IntegrationApi,
  type Direction,
  type EntityContract,
  type FieldMapping,
  type TransformKind,
} from "../api";
import { NAMESPACE } from "../i18n";
import { INTEGRATION_PATHS } from "../paths";
import "./integration.css";

const DIRECTIONS: readonly Direction[] = ["in", "out", "both", "local"];

const TRANSFORMS: readonly TransformKind[] = [
  "none",
  "trim",
  "upper",
  "lower",
  "title",
  "slug",
  "date",
  "decimal",
  "scale",
  "affix",
  "value_map",
  "boolean",
];

/** The entity picker: every entity of the contract, with its direction. */
export function MappingIndexScreen({ client }: { client: ApiClient }) {
  const { t } = useTranslation(NAMESPACE);
  const api = useMemo(() => new IntegrationApi(client), [client]);

  const fetcher = useCallback(() => api.listEntities(), [api]);
  const entities = useResource(fetcher);

  const columns: Column<EntityContract>[] = [
    {
      key: "entity",
      header: t("common.entity"),
      render: (row) => (
        <Link to={INTEGRATION_PATHS.entityMapping} params={{ entity: row.code }}>
          {row.label}
        </Link>
      ),
    },
    {
      key: "code",
      header: t("mapping.column.external"),
      render: (row) => <code className="hardauto-code">{row.code}</code>,
    },
    {
      key: "direction",
      header: t("mapping.column.direction"),
      render: (row) => <Badge tone="neutral">{t(`direction.${row.direction}`)}</Badge>,
    },
    {
      key: "fields",
      header: t("sync.column.fields"),
      render: (row) =>
        row.field_count === 0 ? (
          <span className="hardauto-hint">{t("sync.passthrough")}</span>
        ) : (
          t("sync.fieldCount", { count: row.field_count })
        ),
    },
    {
      key: "state",
      header: t("sync.column.state"),
      render: (row) => (
        <StatusDot
          label={row.is_enabled ? t("sync.enabled") : t("sync.disabled")}
          tone={row.is_enabled ? "success" : "neutral"}
        />
      ),
    },
  ];

  return (
    <Page title={t("mapping.title")} description={t("mapping.intro")} eyebrow={t("group")}>
      <Card title={t("mapping.pick")} description={t("mapping.pickIntro")}>
        <DataTable<EntityContract>
          caption={t("mapping.pick")}
          columns={columns}
          rows={entities.data?.items ?? []}
          rowKey={(row) => row.code}
          loading={entities.loading}
          loadingLabel={t("mapping.pick")}
          error={
            entities.failed
              ? { title: t("error.load"), onRetry: entities.reload, retryLabel: t("error.retry") }
              : undefined
          }
          empty={{ title: t("mapping.pick") }}
        />
      </Card>
    </Page>
  );
}

/** One entity's mapping, editable row by row. */
export function MappingScreen({ client, entity }: { client: ApiClient; entity: string }) {
  const { t } = useTranslation(NAMESPACE);
  const api = useMemo(() => new IntegrationApi(client), [client]);
  const toast = useToast();
  const action = useAction();
  const mayWrite = useCan("integration.mapping.write");

  const fieldsFetcher = useCallback(() => api.listFields(entity), [api, entity]);
  const fields = useResource(fieldsFetcher);

  const entitiesFetcher = useCallback(() => api.listEntities(), [api]);
  const entities = useResource(entitiesFetcher);
  const contract = entities.data?.items.find((row) => row.code === entity);

  const [external, setExternal] = useState("");
  const [portal, setPortal] = useState("");
  const [direction, setDirection] = useState<Direction>("both");
  const [transform, setTransform] = useState<TransformKind>("none");
  const [argument, setArgument] = useState("");

  const portalFields = fields.data?.portal_fields ?? [];

  /** Patch one row and reload, with the row's own busy key. */
  const patch = useCallback(
    (row: FieldMapping, body: Parameters<IntegrationApi["updateField"]>[1]) =>
      action.run(row.id, async () => {
        try {
          await api.updateField(row.id, body);
          fields.reload();
        } catch {
          toast.show(t("error.title"), { tone: "danger" });
        }
      }),
    [action, api, fields, t, toast],
  );

  const columns: Column<FieldMapping>[] = [
    {
      key: "external",
      header: t("mapping.column.external"),
      render: (row) => <code className="hardauto-code">{row.external_field}</code>,
    },
    {
      key: "portal",
      header: t("mapping.column.portal"),
      render: (row) =>
        mayWrite && portalFields.length > 0 ? (
          <Select
            className="hardauto-cell-select"
            aria-label={`${t("mapping.column.portal")} — ${row.external_field}`}
            value={row.portal_field}
            disabled={action.isBusy(row.id)}
            onChange={(event) => void patch(row, { portal_field: event.target.value })}
          >
            <option value="">{t("mapping.portalAny")}</option>
            {portalFields.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </Select>
        ) : row.is_dropped ? (
          <span className="hardauto-hint">{t("mapping.dropped")}</span>
        ) : (
          <code className="hardauto-code">{row.portal_field}</code>
        ),
    },
    {
      key: "direction",
      header: t("mapping.column.direction"),
      render: (row) =>
        mayWrite ? (
          <Select
            className="hardauto-cell-select"
            aria-label={`${t("mapping.column.direction")} — ${row.external_field}`}
            value={row.direction}
            disabled={action.isBusy(row.id)}
            onChange={(event) => void patch(row, { direction: event.target.value as Direction })}
          >
            {DIRECTIONS.map((value) => (
              <option key={value} value={value}>
                {t(`direction.${value}`)}
              </option>
            ))}
          </Select>
        ) : (
          <Badge tone="neutral">{t(`direction.${row.direction}`)}</Badge>
        ),
    },
    {
      key: "transform",
      header: t("mapping.column.transform"),
      render: (row) =>
        mayWrite ? (
          <Select
            className="hardauto-cell-select hardauto-cell-select--narrow"
            aria-label={`${t("mapping.column.transform")} — ${row.external_field}`}
            value={row.transform}
            disabled={action.isBusy(row.id)}
            onChange={(event) =>
              void patch(row, { transform: event.target.value as TransformKind })
            }
          >
            {TRANSFORMS.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </Select>
        ) : (
          row.transform
        ),
    },
    {
      key: "argument",
      header: t("mapping.column.argument"),
      render: (row) =>
        mayWrite ? (
          <Input
            aria-label={`${t("mapping.column.argument")} — ${row.external_field}`}
            defaultValue={row.transform_argument}
            disabled={action.isBusy(row.id)}
            // On blur rather than on change: the server validates the argument
            // and would refuse every keystroke of "fr" at "f".
            onBlur={(event) => {
              if (event.target.value === row.transform_argument) return;
              void patch(row, { transform_argument: event.target.value });
            }}
          />
        ) : (
          row.transform_argument || "—"
        ),
    },
    {
      key: "state",
      header: t("mapping.column.state"),
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
          <StatusDot
            label={row.is_active ? t("mapping.active") : t("mapping.inactive")}
            tone={row.is_active ? "success" : "neutral"}
          />
          {mayWrite ? (
            <>
              <Button
                variant="ghost"
                size="sm"
                busy={action.isBusy(row.id)}
                busyLabel={t("common.saving")}
                onClick={() => void patch(row, { is_active: !row.is_active })}
              >
                {row.is_active ? t("mapping.inactive") : t("mapping.active")}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                busy={action.isBusy(`delete:${row.id}`)}
                busyLabel={t("common.saving")}
                onClick={() =>
                  void action.run(`delete:${row.id}`, async () => {
                    try {
                      await api.removeField(row.id);
                      fields.reload();
                    } catch {
                      toast.show(t("error.title"), { tone: "danger" });
                    }
                  })
                }
              >
                {t("common.delete")}
              </Button>
            </>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <Page
      title={t("mapping.entityTitle", { entity: contract?.label ?? entity })}
      description={t("mapping.intro")}
      // The direction alone. Adding its explanation here read as "Les deux
      // sens · Les deux. En cas de conflit…" — the eyebrow is upper-cased and
      // tracked, so a sentence in it is a wall. The explanation is a hint
      // below, where a sentence belongs.
      eyebrow={contract ? `${t("group")} · ${t(`direction.${contract.direction}`)}` : t("group")}
      back={<Link to={INTEGRATION_PATHS.mapping}>{t("mapping.back")}</Link>}
    >
      {contract ? (
        <p className="hardauto-hint">{t(`direction.${contract.direction}_hint`)}</p>
      ) : null}
      {contract && !contract.is_writable ? (
        <p className="hardauto-hint">{t("mapping.notWritable")}</p>
      ) : null}

      <Card title={t("mapping.keyField")} description={t("mapping.keyFieldHint")} compactTitle>
        {mayWrite && contract ? (
          <Field label={t("mapping.keyField")}>
            {(props) => (
              <Input
                {...props}
                defaultValue={contract.external_key_field}
                onBlur={(event) => {
                  if (event.target.value === contract.external_key_field) return;
                  void action.run("key", async () => {
                    try {
                      await api.updateEntity(entity, { external_key_field: event.target.value });
                      entities.reload();
                    } catch {
                      toast.show(t("error.title"), { tone: "danger" });
                    }
                  });
                }}
              />
            )}
          </Field>
        ) : (
          <code className="hardauto-code">{contract?.external_key_field || "—"}</code>
        )}
      </Card>

      <Card
        title={t("mapping.title")}
        description={`${t("mapping.droppedHint")} ${t(`mapping.transformHint.${transform}`)}`}
        actions={
          mayWrite ? (
            <Button
              variant="primary"
              busy={action.isBusy("add")}
              busyLabel={t("common.saving")}
              disabled={!external.trim()}
              onClick={() =>
                void action.run("add", async () => {
                  try {
                    await api.addField(entity, {
                      external_field: external.trim(),
                      portal_field: portal,
                      direction,
                      transform,
                      transform_argument: argument,
                    });
                    setExternal("");
                    setArgument("");
                    fields.reload();
                  } catch {
                    toast.show(t("error.title"), { tone: "danger" });
                  }
                })
              }
            >
              {t("mapping.add")}
            </Button>
          ) : undefined
        }
      >
        {mayWrite ? (
          <div className="hardauto-integration-form">
            <Field label={t("mapping.column.external")} hint={t("mapping.externalPlaceholder")}>
              {(props) => (
                <Input
                  {...props}
                  value={external}
                  onChange={(event) => setExternal(event.target.value)}
                />
              )}
            </Field>
            <Field label={t("mapping.column.portal")}>
              {(props) => (
                <Select
                  {...props}
                  value={portal}
                  onChange={(event) => setPortal(event.target.value)}
                >
                  <option value="">{t("mapping.portalAny")}</option>
                  {portalFields.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t("mapping.column.direction")} hint={t(`direction.${direction}_hint`)}>
              {(props) => (
                <Select
                  {...props}
                  value={direction}
                  onChange={(event) => setDirection(event.target.value as Direction)}
                >
                  {DIRECTIONS.map((value) => (
                    <option key={value} value={value}>
                      {t(`direction.${value}`)}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field
              label={t("mapping.column.transform")}
              hint={t(`mapping.transformHint.${transform}`)}
            >
              {(props) => (
                <Select
                  {...props}
                  value={transform}
                  onChange={(event) => setTransform(event.target.value as TransformKind)}
                >
                  {TRANSFORMS.map((value) => (
                    <option key={value} value={value}>
                      {value}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field label={t("mapping.column.argument")}>
              {(props) => (
                <Input
                  {...props}
                  value={argument}
                  onChange={(event) => setArgument(event.target.value)}
                />
              )}
            </Field>
          </div>
        ) : null}

        <DataTable<FieldMapping>
          caption={t("mapping.title")}
          columns={columns}
          rows={fields.data?.items ?? []}
          rowKey={(row) => row.id}
          loading={fields.loading}
          loadingLabel={t("mapping.title")}
          error={
            fields.failed
              ? { title: t("error.load"), onRetry: fields.reload, retryLabel: t("error.retry") }
              : undefined
          }
          empty={{ title: t("mapping.empty") }}
        />
      </Card>
    </Page>
  );
}
